import { useParams } from 'react-router-dom';

import { PageHeader, PageShell, type PageAction } from '~/components/layout';
import { Card, CardBody, EmptyState, LoadError, TableRowsSkeleton } from '~/components/ui';
import { mergeQuery, useUrlParams } from '~/hooks/useUrlParams';
import AttributesTab from '~/components/pages/feature-flags/AttributesTab';
import EnvironmentsTab from '~/components/pages/feature-flags/EnvironmentsTab';
import FlagsTab from '~/components/pages/feature-flags/FlagsTab';
import OverviewTab from '~/components/pages/feature-flags/OverviewTab';
import PreviewTab from '~/components/pages/feature-flags/PreviewTab';
import ProductsTab from '~/components/pages/feature-flags/ProductsTab';
import RequestsTab from '~/components/pages/feature-flags/RequestsTab';
import SdkKeysTab from '~/components/pages/feature-flags/SdkKeysTab';
import UpdatesTab from '~/components/pages/feature-flags/UpdatesTab';
import UsersTab from '~/components/pages/feature-flags/UsersTab';
import { errorText, newDetail } from '~/components/pages/feature-flags/common';
import { useReference } from '~/components/pages/feature-flags/useReference';
import { routes } from '~/app/routes';

const SECTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'flags', label: 'Flags' },
  { id: 'updates', label: 'Product updates' },
  { id: 'requests', label: 'Feature requests' },
  { id: 'users', label: 'Known users' },
  { id: 'preview', label: 'Preview' },
  { id: 'products', label: 'Products', admin: true },
  { id: 'environments', label: 'Environments', admin: true },
  { id: 'attributes', label: 'Attributes', admin: true },
  { id: 'sdk-keys', label: 'SDK keys', admin: true },
] as const;

type SectionId = (typeof SECTIONS)[number]['id'];

// What the header's primary button creates on each section: a `/new` record page (`record`), or for SDK keys a
// `?detail=new:key` link that opens the transient key viewer.
const CREATE: Partial<Record<SectionId, { kind: string; label: string; record?: true }>> = {
  flags: { kind: 'flag', label: 'Create flag', record: true },
  updates: { kind: 'update', label: 'Create product update', record: true },
  users: { kind: 'user', label: 'Create user', record: true },
  products: { kind: 'product', label: 'Create product', record: true },
  environments: { kind: 'environment', label: 'Create environment', record: true },
  attributes: { kind: 'attribute', label: 'Create attribute', record: true },
  'sdk-keys': { kind: 'key', label: 'Generate key' },
};

const PLACEHOLDER_COLUMNS = Array.from({ length: 5 }, () => ({}));

export const FeatureFlags = () => {
  const url = useUrlParams();
  const { section, area } = useParams();
  // Admin areas live under /feature-flags/admin/:area and are not reachable as /feature-flags/:section.
  const current = SECTIONS.find((s) =>
    area ? 'admin' in s && s.id === area : !('admin' in s) && s.id === (section ?? 'overview'),
  );

  // The page body waits for all three: a tab reads them together.
  const { reference, loaded, error: loadError, refetch } = useReference();

  const create = current && CREATE[current.id];
  const actions: PageAction[] = create
    ? [
        {
          label: create.label,
          kind: 'primary',
          testId: `feature-flags-new-${create.kind}`,
          href: create.record
            ? 'admin' in current
              ? routes.featureFlagsAdminRecordNew(current.id)
              : // The users list's product and environment filters become the new user's defaults.
                `${routes.featureFlagsRecordNew(current.id)}${
                  current.id === 'users'
                    ? mergeQuery('', {
                        product: url.get('product'),
                        environment: url.get('environment'),
                      })
                    : ''
                }`
            : url.href({ detail: newDetail(create.kind) }),
        },
      ]
    : [];

  const sectionProps = { reference };

  return (
    <PageShell>
      <PageHeader
        title={current && current.id !== 'overview' ? current.label : 'Feature flags'}
        breadcrumbs={
          current?.id === 'overview'
            ? [{ label: 'Feature flags' }]
            : [
                { label: 'Feature flags', href: routes.featureFlags },
                ...(current && 'admin' in current ? [{ label: 'Admin' }] : []),
                ...(current ? [{ label: current.label }] : []),
              ]
        }
        actions={actions}
      />

      {loadError && !loaded && (
        <LoadError
          what="the feature flag settings"
          message={errorText(loadError, 'Reload the page, or try again in a moment.')}
          onRetry={refetch}
          testId="feature-flags"
        />
      )}

      {!current ? (
        <EmptyState
          title="This section doesn't exist"
          description="Pick a feature flags section from the menu."
        />
      ) : !loaded && !loadError ? (
        <Card>
          <CardBody>
            <TableRowsSkeleton columns={PLACEHOLDER_COLUMNS} rows={6} />
          </CardBody>
        </Card>
      ) : !loaded ? null /* the load failed: the error banner above (with Try again) is the whole page, not an empty list */ : (
        <>
          {current?.id === 'overview' && <OverviewTab {...sectionProps} />}
          {current?.id === 'flags' && <FlagsTab {...sectionProps} />}
          {current?.id === 'updates' && <UpdatesTab {...sectionProps} />}
          {current?.id === 'requests' && <RequestsTab {...sectionProps} />}
          {current?.id === 'users' && <UsersTab {...sectionProps} />}
          {current?.id === 'preview' && <PreviewTab {...sectionProps} />}
          {current?.id === 'products' && <ProductsTab {...sectionProps} />}
          {current?.id === 'environments' && <EnvironmentsTab {...sectionProps} />}
          {current?.id === 'attributes' && <AttributesTab {...sectionProps} />}
          {current?.id === 'sdk-keys' && <SdkKeysTab {...sectionProps} />}
        </>
      )}
    </PageShell>
  );
};

export default FeatureFlags;
